import React from 'react';
import { unpackToTime } from '@campfire/hot-date';
import { TitularTooltip } from '@campfire/titular-tooltip';
import { Box, Typography } from '@material-ui/core';
import { LocationOnRounded, Explore } from '@material-ui/icons';
import { DateInfoCard } from '../../../../common/cards/date-info-card/DateInfoCard';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { VolunteerRosters } from '../ActivityQueries/__generated__/VolunteerRosters';
import { RosteredStatus } from '../ActivityTypes';
import { getDisplayTimeSchedule } from '../../../../common/functions/activity-display-helpers';
import { CapacityStatusChip } from '../../../../common/chips/CapacityStatusChip';
import { ActivityDateCapacityStatus, summariseCapacity } from '../../../../common/hooks/useActivityCapacityStatus';

import { ActivityTimelineItem as ActivityTimelineItemFragments } from '../ActivityTimeline/__generated__/ActivityTimelineItem';

export type MyShiftItemProps = ActivityTimelineItemFragments & {
  activityDate: string;
  rosterTemplateAddress?: string;
  rosterTemplateName?: string | null;
  publishedRoster?: { name?: string | null } | null;
};

export interface MyShiftItemMenuProps {
  rosteredStatus: RosteredStatus;
  onUpdateAvailability: () => void;
  onUnavailable: () => void;
  onWithdraw: () => void;
}

export function MyShiftItem(props: {
  activity: MyShiftItemProps;
  ActivityMenu: any;
  onSelect: () => void;
  isSelected: boolean;
  color: any;
  rostering?: VolunteerRosters;
  conflict?: boolean;
  capacityStatus?: ActivityDateCapacityStatus;
}) {
  const { activity, onSelect, isSelected, ActivityMenu, color, rostering, conflict, capacityStatus } = props;
  const { theme, isMobile, isMd } = useCampfireTheme();
  const formatTime = (time: string) => unpackToTime(time).toFormat('h:mm a');

  const renderActivityTime = (
    activityStartTime: string,
    activityEndTime: string,
    activitySessions?: any[],
    dateForActivity?: string
  ) => {
    // Check if user has individual shift times on their rosterings
    const sessionRosteringsWithTimes = rostering?.sessionRosterings.filter((sr) => sr.startTime || sr.endTime);

    // If any session rosterings have custom times, compute range across ALL rostered sessions
    if (sessionRosteringsWithTimes && sessionRosteringsWithTimes.length > 0) {
      let earliestStart = '';
      let latestEnd = '';

      if (rostering) {
        rostering.sessionRosterings.forEach((sr) => {
          if (!sr?.session) return;
          // Use custom times if set, otherwise look up session default times
          const matchedSession = activitySessions?.find((s: any) => s.sessionId === sr.session.sessionId);
          const srStart = sr.startTime || matchedSession?.startTime;
          const srEnd = sr.endTime || matchedSession?.endTime;
          if (srStart && (!earliestStart || srStart < earliestStart)) {
            earliestStart = srStart;
          }
          if (srEnd && (!latestEnd || srEnd > latestEnd)) {
            latestEnd = srEnd;
          }
        });
      }

      if (earliestStart && latestEnd) {
        return `${formatTime(earliestStart)} - ${formatTime(latestEnd)}`;
      }
      if (earliestStart) {
        return `From ${formatTime(earliestStart)}`;
      }
      if (latestEnd) {
        return `Until ${formatTime(latestEnd)}`;
      }
    }

    // Check if user is rostered for any sessions
    const rosteredSessionIds = rostering?.sessionRosterings.map((sr) => sr.session.sessionId) || [];

    // Find all rostered sessions
    const rosteredSessions =
      activitySessions?.filter((session) => rosteredSessionIds.includes(session.sessionId)) || [];

    // If we have rostered sessions, calculate the latest end time
    let latestEndTime = '';
    let earliestStartTime = '';

    // First check if we have sessions with flexible instances for the current date
    if (activitySessions && dateForActivity) {
      // Look for flexible sessions with instances for this date
      const flexibleSessionsWithInstances = activitySessions.filter(
        (session) =>
          session.isFlexible &&
          session.currentFlexibleInstances &&
          session.currentFlexibleInstances.length > 0 &&
          session.currentFlexibleInstances[0] &&
          (!session.currentFlexibleInstances[0].sessionDate ||
            session.currentFlexibleInstances[0].sessionDate === dateForActivity)
      );

      if (flexibleSessionsWithInstances.length > 0) {
        // For rostered sessions, track the earliest start and latest end time
        if (rosteredSessions.length > 0) {
          // Get the earliest start time and latest end time from all rostered sessions
          rosteredSessions.forEach((session) => {
            // For flexible sessions, use the instance time
            if (session.isFlexible && session.currentFlexibleInstances && session.currentFlexibleInstances.length > 0) {
              const instance = session.currentFlexibleInstances[0];
              if (instance.startTime && (!earliestStartTime || instance.startTime < earliestStartTime)) {
                earliestStartTime = instance.startTime;
              }
              if (instance.endTime && (!latestEndTime || instance.endTime > latestEndTime)) {
                latestEndTime = instance.endTime;
              }
            }
            // For regular sessions, use the session time
            else {
              if (session.startTime && (!earliestStartTime || session.startTime < earliestStartTime)) {
                earliestStartTime = session.startTime;
              }
              if (session.endTime && (!latestEndTime || session.endTime > latestEndTime)) {
                latestEndTime = session.endTime;
              }
            }
          });

          // If we have calculated valid times, return the time range
          if (earliestStartTime && latestEndTime) {
            return `${formatTime(earliestStartTime)} - ${formatTime(latestEndTime)}`;
          }
        }

        // If no rostered sessions or couldn't calculate times, show flexible session times
        const flexibleTimes = flexibleSessionsWithInstances
          .map((session) => {
            if (!session.currentFlexibleInstances || !session.currentFlexibleInstances.length) {
              return null;
            }
            const flexibleInstance = session.currentFlexibleInstances[0];
            if (flexibleInstance && flexibleInstance.startTime && flexibleInstance.endTime) {
              const timeStr = getDisplayTimeSchedule(
                flexibleInstance.startTime,
                flexibleInstance.endTime,
                true,
                flexibleInstance
              );

              // If we have multiple sessions, prefix with session name
              const displayName = flexibleInstance.name || session.name;
              return flexibleSessionsWithInstances.length > 1 ? `${displayName}: ${timeStr}` : timeStr;
            }
            return null;
          })
          .filter(Boolean);

        if (flexibleTimes.length > 0) {
          return flexibleTimes.join(', ');
        }
      }
    }

    // Default to regular activity time
    return activityStartTime && activityEndTime
      ? `${formatTime(activityStartTime)} - ${formatTime(activityEndTime)}`
      : '';
  };

  return (
    <Box marginTop='0.9375rem' onClick={onSelect}>
      <DateInfoCard date={activity.activityDate} size='l' color={color} selected={isSelected} conflict={conflict}>
        <Box display='flex' justifyContent='space-between' alignSelf='stretch'>
          <TitularTooltip
            title={
              rostering?.publishedRoster?.name ||
              activity.publishedRoster?.name ||
              activity.rosterTemplateName ||
              activity.name
            }
          >
            <Box flex={1} minWidth={0} mr={2}>
              <Typography
                variant={'h6'}
                noWrap
                style={{ color: theme.color.grey.neutral400, fontWeight: 800, width: '100%', letterSpacing: '-0.02em' }}
              >
                {rostering?.publishedRoster?.name ||
                  activity.publishedRoster?.name ||
                  activity.rosterTemplateName ||
                  activity.name}
              </Typography>
              {(rostering?.publishedRoster?.name || activity.publishedRoster?.name || activity.rosterTemplateName) && (
                <Typography variant='body2' style={{ color: theme.color.grey.neutral300 }}>
                  {activity.name}
                </Typography>
              )}
            </Box>
          </TitularTooltip>
          <Box display='flex' flexDirection='column' alignItems='stretch' flexShrink={0}>
            <Box
              style={{
                position: 'relative',
                zIndex: 1,
                backgroundColor: theme.color.white.neutral50,
                borderRadius: 48,
              }}
            >
              {ActivityMenu}
            </Box>
            {capacityStatus?.overallStatus && (
              <CapacityStatusChip
                status={capacityStatus.overallStatus}
                {...summariseCapacity(capacityStatus.sessions)}
                size='small'
              />
            )}
          </Box>
        </Box>
        {rostering ? (
          <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200 }}>
            Attending:{' '}
            {rostering.sessionRosterings
              .map((sessionRostering) => {
                let sessionName = sessionRostering.session.name;

                // Check if there's a matching session with flexible instances
                if (activity.sessions && activity.sessions.length > 0) {
                  const { sessionId } = sessionRostering.session;
                  const matchingSession = activity.sessions.find((session) => session.sessionId === sessionId);

                  // If we have a matching session with flexible instances
                  if (
                    matchingSession &&
                    matchingSession.isFlexible &&
                    matchingSession.currentFlexibleInstances &&
                    matchingSession.currentFlexibleInstances.length > 0
                  ) {
                    // Get the first flexible instance (which should be for the current date)
                    const flexibleInstance = matchingSession.currentFlexibleInstances[0];

                    // Use the instance name if available, or fall back to the session name
                    if (flexibleInstance.name) {
                      sessionName = flexibleInstance.name;
                    }
                  }
                }

                // Add individual shift times if set
                const sr = sessionRostering as any;
                if (sr.startTime || sr.endTime) {
                  const startStr = sr.startTime ? formatTime(sr.startTime) : '';
                  const endStr = sr.endTime ? formatTime(sr.endTime) : '';
                  if (startStr && endStr) {
                    return `${sessionName} (${startStr} - ${endStr})`;
                  }
                  if (startStr) {
                    return `${sessionName} (from ${startStr})`;
                  }
                  if (endStr) {
                    return `${sessionName} (until ${endStr})`;
                  }
                }

                return sessionName;
              })
              .join(', ')}
          </Typography>
        ) : null}
        <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200 }}>
          {renderActivityTime(activity.startTime, activity.endTime, activity.sessions, activity.activityDate)}
        </Typography>
        <Box display={isMobile || isMd ? 'auto' : 'flex'} alignItems='center' mt='40px'>
          <Box display='flex' alignItems='center' pr={2} pt={isMobile || isMd ? 1 : 0}>
            <LocationOnRounded style={{ color: theme.color.grey.neutral200, fontSize: 14, marginRight: '6px' }} />
            <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200, marginTop: '1px' }}>
              {rostering
                ? activity?.rosterTemplateAddress
                  ? activity.rosterTemplateAddress
                  : activity.activityLocation
                  ? activity.activityLocation.placesAddress.description
                  : activity.activityRemoteLocation?.details || null
                : activity.effectiveAddressRevealBeforeDays != null || activity.effectiveAddressHideAfterDays != null
                ? (() => {
                    if (activity.effectiveAddressRevealBeforeDays != null && activity.activityDate) {
                      const rosterDate = new Date(`${activity.activityDate}T00:00:00`);
                      const revealDate = new Date(rosterDate);
                      revealDate.setDate(revealDate.getDate() - activity.effectiveAddressRevealBeforeDays);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const daysUntil = Math.ceil((revealDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                      if (daysUntil > 0) {
                        return `Address available in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`;
                      }
                    }
                    return 'Address not available';
                  })()
                : null}
            </Typography>
          </Box>
          {/* <Box display='flex' alignItems='center' pr={3} pt={isMobile || isMd ? 1 : 0}>
            <AccessTimeIcon style={{ color: theme.color.grey.neutral200, fontSize: 14, marginRight: '6px' }} />
            <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200, marginTop: '1px' }}>
              {activity.hasOpenRoster ? 'Open Roster' : 'Managed Roster'}
            </Typography>
          </Box> */}
          <Box display='flex' alignItems='center' pr={2} pt={isMobile || isMd ? 1 : 0}>
            <Typography
              variant={'h2'}
              style={{ fontWeight: 600, color: theme.color.grey.neutral200, fontSize: 14, marginRight: '6px' }}
            >
              {activity.sessions.length}
            </Typography>
            <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200, marginTop: '1px' }}>
              {activity.sessions.length > 1 ? 'sessions available' : 'session available'}
            </Typography>
          </Box>
          <Box display='flex' alignItems='center' pr={2} pt={isMobile || isMd ? 1 : 0}>
            <Explore style={{ color: theme.color.grey.neutral200, fontSize: 14, marginRight: '6px' }} />
            <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200, marginTop: '1px' }}>
              {activity?.program.name}
            </Typography>
          </Box>
        </Box>
      </DateInfoCard>
    </Box>
  );
}
